import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { AlertTriangle, AlertCircle, Shield } from 'lucide-react';

export default function AccidentChart() {
  const [animatedData, setAnimatedData] = useState([]);
  const [hoveredBarKey, setHoveredBarKey] = useState(null); // `${index}-accidents|incidents|fatalities`
  const [tooltipData, setTooltipData] = useState(null);     // { year, rawValue, label, x, y }
  const [isMobile, setIsMobile] = useState(false);

  // gutter dinâmico para dar espaço acima no mobile
  const [extraTop, setExtraTop] = useState(56);

  const scrollRef = useRef(null);
  const innerRefDesktop = useRef(null);
  const innerRefMobile  = useRef(null);
  const tooltipRef      = useRef(null);

  const accidentData = [
    { year: 2010, accidents: 128, incidents: 363, fatalities: 55 }, 
    { year: 2011, accidents: 187, incidents: 324, fatalities: 110 },
    { year: 2012, accidents: 205, incidents: 365, fatalities: 110 }, 
    { year: 2013, accidents: 199, incidents: 386, fatalities: 94 },
    { year: 2014, accidents: 175, incidents: 318, fatalities: 82 }, 
    { year: 2015, accidents: 172, incidents: 250, fatalities: 79 },
    { year: 2016, accidents: 164, incidents: 192, fatalities: 104 }, 
    { year: 2017, accidents: 146, incidents: 232, fatalities: 54 },
    { year: 2018, accidents: 167, incidents: 198, fatalities: 81 }, 
    { year: 2019, accidents: 150, incidents: 265, fatalities: 66 },
    { year: 2020, accidents: 150, incidents: 284, fatalities: 55 }, 
    { year: 2021, accidents: 142, incidents: 329, fatalities: 60 },
    { year: 2022, accidents: 137, incidents: 394, fatalities: 49 }, 
    { year: 2023, accidents: 155, incidents: 1176, fatalities: 77 },
    { year: 2024, accidents: 175, incidents: 2445, fatalities: 152 }
  ];

  const SCALE_INCIDENTS_DIV = 10; // ÷10
  const SCALE_FATALITIES_MUL = 2; // ×2

  const CHART_HEIGHT = 320;
  const maxValue = Math.max(
    ...accidentData.map(d =>
      Math.max(
        d.accidents,
        d.incidents / SCALE_INCIDENTS_DIV,
        d.fatalities * SCALE_FATALITIES_MUL
      )
    )
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setAnimatedData(accidentData), 400);
    return () => clearTimeout(t);
  }, []);

  const getBarHeight = (v) => (v / maxValue) * (CHART_HEIGHT - 40);

  const clsAcc = (h) => `rounded-t transition-all duration-300 cursor-pointer ${h ? 'bg-red-600' : 'bg-red-500'}`;
  const clsInc = (h) => `rounded-t transition-all duration-300 cursor-pointer ${h ? 'bg-yellow-600' : 'bg-yellow-500'}`;
  const clsFat = (h) => `rounded-t transition-all duration-300 cursor-pointer ${h ? 'bg-gray-700' : 'bg-gray-600'}`;

  function positionTooltip(item, key, e) {
    const inner = (isMobile ? innerRefMobile : innerRefDesktop).current;
    if (!inner) return;

    const barRect   = e.currentTarget.getBoundingClientRect();
    const innerRect = inner.getBoundingClientRect();

    // centro X relativo ao inner; topo preferindo acima
    let centerX = (barRect.left - innerRect.left) + (barRect.width / 2);

    setHoveredBarKey(key);
    const type = key.split('-')[1];
    const label =
      type === 'accidents' ? 'Acidentes' :
      type === 'incidents' ? `Incidentes (÷${SCALE_INCIDENTS_DIV})` :
                             `Fatalidades (×${SCALE_FATALITIES_MUL})`;

    // Render offscreen para medir
    setTooltipData({
      year: item.year,
      rawValue: item[type],
      label,
      x: -9999,
      y: -9999
    });

// espera dois frames para garantir que o tooltip já montou no DOM
requestAnimationFrame(() => {
  requestAnimationFrame(() => {
    if (!tooltipRef.current) return;
    const tipW = tooltipRef.current.clientWidth;
    const tipH = tooltipRef.current.clientHeight;

    if (isMobile) setExtraTop(Math.max(56, tipH + 12));

    const aboveTop = (barRect.top - innerRect.top) - 10 - tipH;
    const belowTop = (barRect.bottom - innerRect.top) + 10;

    let minLeft = 8 + tipW / 2;
    let maxRight = innerRect.width - 8 - tipW / 2;
    let minTop = 8;
    let maxTop = innerRect.height - tipH - 8;

    if (isMobile && scrollRef.current) {
      const wrapRect = scrollRef.current.getBoundingClientRect();
      const visibleLeftInInner   = wrapRect.left  - innerRect.left;
      const visibleRightInInner  = wrapRect.right - innerRect.left;
      const visibleTopInInner    = wrapRect.top   - innerRect.top;
      const visibleBottomInInner = wrapRect.bottom- innerRect.top;

      minLeft = visibleLeftInInner  + 8 + tipW / 2;
      maxRight= visibleRightInInner - 8 - tipW / 2;
      minTop  = visibleTopInInner + 8;
      maxTop  = visibleBottomInInner - tipH - 8;
    }

    const placeAbove = !(aboveTop < minTop);
    let top = placeAbove ? aboveTop : belowTop;

    centerX = Math.max(minLeft, Math.min(centerX, maxRight));
    top     = Math.max(minTop,  Math.min(top,     maxTop));

    setTooltipData(prev => prev ? { ...prev, x: centerX, y: top, placeAbove } : prev);
  });
});

  }

  const onEnterOrTouch = (item, key) => (e) => positionTooltip(item, key, e);
  const onLeave = () => setTooltipData(null);

  // --- MOBILE ---
  const BAR_W_MOBILE = 14;
  const GAP_BETWEEN_BARS = 6;
  const GAP_BETWEEN_GROUPS = 14;
  const GROUP_W_MOBILE = (BAR_W_MOBILE * 3) + (GAP_BETWEEN_BARS * 2) + GAP_BETWEEN_GROUPS;
  const contentWidthMobile = (accidentData.length * GROUP_W_MOBILE) + 16;

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-center">Acidentes Aéreos no Brasil</CardTitle>
          <p className="text-center text-gray-600">Evolução de acidentes, incidentes e fatalidades registrados pelo CENIPA</p>
        </CardHeader>
        <CardContent>
          {isMobile ? (
            <div className="overflow-x-auto no-scrollbar" ref={scrollRef} onScroll={onLeave}>
              <div
                className="relative flex items-end px-4"
                ref={innerRefMobile}
                style={{
                  height: CHART_HEIGHT + extraTop,
                  width: contentWidthMobile,
                  paddingTop: extraTop
                }}
              >
                {accidentData.map((item, index) => (
                  <div key={item.year} className="flex flex-col items-center justify-end" style={{ width: GROUP_W_MOBILE }}>
                    <div className="flex items-end" style={{ gap: GAP_BETWEEN_BARS }}>
                      <div
                        className={`${clsAcc(hoveredBarKey === `${index}-accidents`)}`}
                        style={{ width: BAR_W_MOBILE, height: `${animatedData[index] ? getBarHeight(item.accidents) : 0}px`, transitionDelay: `${index * 40}ms` }}
                        onTouchStart={onEnterOrTouch(item, `${index}-accidents`)}
                        onMouseEnter={onEnterOrTouch(item, `${index}-accidents`)}
                        onMouseLeave={onLeave}
                      />
                      <div
                        className={`${clsInc(hoveredBarKey === `${index}-incidents`)}`}
                        style={{ width: BAR_W_MOBILE, height: `${animatedData[index] ? getBarHeight(item.incidents / SCALE_INCIDENTS_DIV) : 0}px`, transitionDelay: `${index * 40 + 12}ms` }}
                        onTouchStart={onEnterOrTouch(item, `${index}-incidents`)}
                        onMouseEnter={onEnterOrTouch(item, `${index}-incidents`)}
                        onMouseLeave={onLeave}
                      />
                      <div
                        className={`${clsFat(hoveredBarKey === `${index}-fatalities`)}`}
                        style={{ width: BAR_W_MOBILE, height: `${animatedData[index] ? getBarHeight(item.fatalities * SCALE_FATALITIES_MUL) : 0}px`, transitionDelay: `${index * 40 + 24}ms` }}
                        onTouchStart={onEnterOrTouch(item, `${index}-fatalities`)}
                        onMouseEnter={onEnterOrTouch(item, `${index}-fatalities`)}
                        onMouseLeave={onLeave}
                      />
                    </div>
                    <span className="text-[10px] mt-1 text-gray-500">{String(item.year).slice(2)}</span>
                  </div>
                ))}

                {tooltipData && (
                  <div
                    ref={tooltipRef}
                    className="absolute z-10 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg pointer-events-none transition-all duration-200 whitespace-nowrap"
                    style={{ left: tooltipData.x, top: tooltipData.y, transform: 'translate(-50%,-10%)' }}
                  >
                    <div className="text-sm font-semibold">{tooltipData.year}</div>
                    <div className="text-xs">{tooltipData.label}: {tooltipData.rawValue.toLocaleString('pt-BR')}</div>
                    {/* sem seta */}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="h-80 px-4 relative" ref={innerRefDesktop}>
              <div className="flex items-end justify-between h-full gap-2">
                {accidentData.map((item, index) => (
                  <div key={item.year} className="flex flex-col items-center justify-end h-full">
                    <div className="flex items-end gap-1 h-full">
                      <div
                        className={`w-4 ${clsAcc(hoveredBarKey === `${index}-accidents`)} hover:opacity-90`}
                        style={{ height: `${animatedData[index] ? getBarHeight(item.accidents) : 0}px`, transitionDelay: `${index * 50}ms` }}
                        onMouseEnter={onEnterOrTouch(item, `${index}-accidents`)}
                        onMouseLeave={onLeave}
                      />
                      <div
                        className={`w-4 ${clsInc(hoveredBarKey === `${index}-incidents`)} hover:opacity-90`}
                        style={{ height: `${animatedData[index] ? getBarHeight(item.incidents / SCALE_INCIDENTS_DIV) : 0}px`, transitionDelay: `${index * 50 + 16}ms` }}
                        onMouseEnter={onEnterOrTouch(item, `${index}-incidents`)}
                        onMouseLeave={onLeave}
                      />
                      <div
                        className={`w-4 ${clsFat(hoveredBarKey === `${index}-fatalities`)} hover:opacity-90`}
                        style={{ height: `${animatedData[index] ? getBarHeight(item.fatalities * SCALE_FATALITIES_MUL) : 0}px`, transitionDelay: `${index * 50 + 32}ms` }}
                        onMouseEnter={onEnterOrTouch(item, `${index}-fatalities`)}
                        onMouseLeave={onLeave}
                      />
                    </div>
                    <span className="text-xs mt-2 text-gray-500">{item.year}</span>
                  </div>
                ))}
              </div>

              {tooltipData && (
                <div
                  ref={tooltipRef}
                  className="absolute z-10 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg pointer-events-none transition-all duration-200 whitespace-nowrap"
                   style={{ left: tooltipData.x,
 top: tooltipData.y,
  transform: tooltipData.placeAbove ? 'translate(-50%,-100%)' : 'translate(-50%, 0)'
 }}
                >
                  <div className="text-sm font-semibold">{tooltipData.year}</div>
                  <div className="text-xs">{tooltipData.label}: {tooltipData.rawValue.toLocaleString('pt-BR')}</div>
                </div>
              )}
            </div>
          )}

          {/* Legenda */}
          <div className="mt-6 flex justify-center gap-6 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-500 rounded"></div>
              <AlertTriangle className="w-4 h-4 mr-1" />
              <span>Acidentes</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-yellow-500 rounded"></div>
              <AlertCircle className="w-4 h-4 mr-1" />
              <span>Incidentes (÷{SCALE_INCIDENTS_DIV})</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-600 rounded"></div>
              <Shield className="w-4 h-4 mr-1" />
              <span>Fatalidades (×{SCALE_FATALITIES_MUL})</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
